import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Container, Typography, Grid, CircularProgress, Alert, Paper, LinearProgress,
  Dialog, DialogContent, DialogTitle, IconButton, FormControl, InputLabel, Select,
  MenuItem, Button, Checkbox, ListItemText, OutlinedInput, Chip, Tabs, Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HistoricoPerformanceChart from '../components/charts/HistoricoPerformanceChart';
import DrillDownChart from '../components/charts/DrillDownChart';
import { useMultipleQueries } from '../hooks/useApiCache';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Paleta de cores consistente para contratos (mesma do HistoricoPerformanceChart)
const COLORS = [
  '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
  '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'
];

const Cards = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChart, setSelectedChart] = useState(null);

  // Lista de contratos disponiveis
  const [contratos, setContratos] = useState([]);
  const [contratosLoading, setContratosLoading] = useState(false);

  // Filtros selecionados (pendentes)
  const [selectedContratos, setSelectedContratos] = useState([]);

  // Filtros aplicados (usados na query)
  const [appliedContratos, setAppliedContratos] = useState([]);

  // Controle de carregamento
  const [shouldLoadData, setShouldLoadData] = useState(true);
  const [loadCounter, setLoadCounter] = useState(0);

  // Estado compartilhado para ocultar/mostrar contratos em todos os gráficos
  const [hiddenContratos, setHiddenContratos] = useState({});

  // Callback para toggle de contrato (sincroniza todos os gráficos)
  const handleToggleContrato = useCallback((contrato) => {
    setHiddenContratos(prev => ({
      ...prev,
      [contrato]: !prev[contrato]
    }));
  }, []);

  // Mapa de cores global para garantir consistência entre todos os gráficos
  const globalColorMap = useMemo(() => {
    const colorMap = {};
    contratos.forEach((contrato, index) => {
      colorMap[contrato] = COLORS[index % COLORS.length];
    });
    return colorMap;
  }, [contratos]);

  // Carregar lista de contratos e auto-selecionar todos
  useEffect(() => {
    const fetchContratos = async () => {
      setContratosLoading(true);
      try {
        const response = await fetch(`${API_URL}/contratos`);
        const data = await response.json();
        if (Array.isArray(data)) {
          const sorted = data.sort();
          setContratos(sorted);
          // Auto-selecionar todos os contratos por padrão
          if (selectedContratos.length === 0) {
            setSelectedContratos(sorted);
            setAppliedContratos(sorted);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar contratos:', error);
      } finally {
        setContratosLoading(false);
      }
    };
    fetchContratos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = (chartConfig) => {
    setSelectedChart(chartConfig);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedChart(null);
  };

  // Aplicar filtros e carregar dados
  const handleCarregar = useCallback(() => {
    setAppliedContratos(selectedContratos);
    setShouldLoadData(true);
    setLoadCounter(prev => prev + 1);
  }, [selectedContratos]);

  // Filtros para a query (usa 'contrato' pois buildPayload converte para 'des_contrato')
  const filters = {
    contrato: appliedContratos.length > 0 ? appliedContratos : undefined
  };

  // Opcoes de query
  const queryOptions = {
    enabled: shouldLoadData,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    _key: loadCounter
  };

  const { data, isLoading } = useMultipleQueries([
    { endpoint: '/historico-mensal-performance', filters, options: queryOptions },
    { endpoint: '/historico-tempo-primeiro-servico', filters, options: queryOptions }
  ]);

  // Atualizar hasLoadedOnce quando terminar de carregar
  useEffect(() => {
    if (shouldLoadData && !isLoading && data && data.length > 0) {
      setHasLoadedOnce(true);
      setShouldLoadData(false);
    }
  }, [shouldLoadData, isLoading, data]);

  // Desestruturar dados
  const [
    historicoMensalData = [],
    historicoTempoPrimeiroServico = []
  ] = data || [];

  return (
    <Container maxWidth="xl" sx={{ mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Evolucao Historica
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Acompanhamento dos principais indicadores dos ultimos 6 meses por contrato
        </Typography>
      </Box>

      {/* Tabs de Navegacao */}
      <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          sx={{ px: 2 }}
        >
          <Tab label="Visao Geral" />
          <Tab label="Detalhamento" />
        </Tabs>
      </Paper>

      {/* Conteudo da Aba Visao Geral */}
      {activeTab === 0 && (
        <>
          {/* Barra de Filtros */}
      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={8}>
            <FormControl fullWidth size="small">
              <InputLabel>Contratos</InputLabel>
              <Select
                multiple
                value={selectedContratos}
                onChange={(e) => {
                  const value = e.target.value;
                  // Verifica se clicou em "Selecionar Todos"
                  if (value.includes('__SELECT_ALL__')) {
                    if (selectedContratos.length === contratos.length) {
                      setSelectedContratos([]);
                    } else {
                      setSelectedContratos([...contratos]);
                    }
                  } else {
                    setSelectedContratos(value);
                  }
                }}
                input={<OutlinedInput label="Contratos" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.length === contratos.length && contratos.length > 0 ? (
                      <Chip label="Todos selecionados" size="small" color="primary" />
                    ) : selected.length <= 3 ? (
                      selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))
                    ) : (
                      <Chip label={`${selected.length} selecionados`} size="small" />
                    )}
                  </Box>
                )}
                disabled={contratosLoading}
              >
                <MenuItem value="__SELECT_ALL__">
                  <Checkbox
                    checked={contratos.length > 0 && selectedContratos.length === contratos.length}
                    indeterminate={selectedContratos.length > 0 && selectedContratos.length < contratos.length}
                  />
                  <ListItemText primary="Selecionar Todos" primaryTypographyProps={{ fontWeight: 'bold' }} />
                </MenuItem>
                {contratos.map((contrato) => (
                  <MenuItem key={contrato} value={contrato}>
                    <Checkbox checked={selectedContratos.indexOf(contrato) > -1} />
                    <ListItemText primary={contrato} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCarregar}
              startIcon={<SearchIcon />}
              fullWidth
              sx={{ height: 40 }}
            >
              Carregar
            </Button>
          </Grid>
        </Grid>
        {appliedContratos.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Filtro aplicado: {appliedContratos.length} contrato(s)
          </Typography>
        )}
      </Paper>

      {/* Barra de Progresso durante carregamento */}
      {isLoading && (
        <Paper elevation={3} sx={{ mb: 4, p: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} />
            Carregando dados historicos...
          </Typography>
          <LinearProgress sx={{ mt: 2, height: 8, borderRadius: 1 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Buscando dados dos ultimos 6 meses. Por favor, aguarde.
          </Typography>
        </Paper>
      )}

      {/* Mensagem de erro se nao houver dados */}
      {!isLoading && hasLoadedOnce && historicoMensalData.length === 0 && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="body1">
            Nenhum dado historico encontrado. Verifique se a Materialized View foi criada corretamente.
          </Typography>
        </Alert>
      )}

      {/* Graficos Historicos */}
      {!isLoading && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <HistoricoPerformanceChart
              data={historicoMensalData}
              dataKey="percentual_faturamento"
              title="% Faturamento"
              yAxisLabel="%"
              loading={isLoading}
              height={450}
              hiddenContratos={hiddenContratos}
              onToggleContrato={handleToggleContrato}
              colorMap={globalColorMap}
              onClick={() => handleOpenModal({
                data: historicoMensalData,
                dataKey: 'percentual_faturamento',
                title: '% Faturamento',
                yAxisLabel: '%'
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistoricoPerformanceChart
              data={historicoMensalData}
              dataKey="percentual_turnos"
              title="% Turnos Realizados"
              yAxisLabel="%"
              loading={isLoading}
              height={450}
              hiddenContratos={hiddenContratos}
              onToggleContrato={handleToggleContrato}
              colorMap={globalColorMap}
              onClick={() => handleOpenModal({
                data: historicoMensalData,
                dataKey: 'percentual_turnos',
                title: '% Turnos Realizados',
                yAxisLabel: '%'
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistoricoPerformanceChart
              data={historicoMensalData}
              dataKey="tempo_medio_saida_base"
              title="Tempo Medio de Saida da Base"
              yAxisLabel="min"
              loading={isLoading}
              height={450}
              hiddenContratos={hiddenContratos}
              onToggleContrato={handleToggleContrato}
              colorMap={globalColorMap}
              onClick={() => handleOpenModal({
                data: historicoMensalData,
                dataKey: 'tempo_medio_saida_base',
                title: 'Tempo Medio de Saida da Base',
                yAxisLabel: 'min'
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <HistoricoPerformanceChart
              data={historicoTempoPrimeiroServico}
              dataKey="tempo_medio_primeiro_servico"
              title="Tempo ate Primeiro Servico"
              yAxisLabel="min"
              loading={isLoading}
              height={450}
              hiddenContratos={hiddenContratos}
              onToggleContrato={handleToggleContrato}
              colorMap={globalColorMap}
              onClick={() => handleOpenModal({
                data: historicoTempoPrimeiroServico,
                dataKey: 'tempo_medio_primeiro_servico',
                title: 'Tempo ate Primeiro Servico',
                yAxisLabel: 'min'
              })}
            />
          </Grid>
        </Grid>
      )}

      {/* Modal para visualizacao ampliada */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: '80vh'
          }
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h4" component="span" sx={{ fontWeight: 600 }}>
            {selectedChart?.title}
          </Typography>
          <IconButton
            aria-label="fechar"
            onClick={handleCloseModal}
            size="large"
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 4 }}>
          {selectedChart && (
            <HistoricoPerformanceChart
              data={selectedChart.data}
              dataKey={selectedChart.dataKey}
              title=""
              yAxisLabel={selectedChart.yAxisLabel}
              loading={false}
              height={600}
              hiddenContratos={hiddenContratos}
              onToggleContrato={handleToggleContrato}
              colorMap={globalColorMap}
            />
          )}
        </DialogContent>
      </Dialog>
        </>
      )}

      {/* Conteudo da Aba Detalhamento */}
      {activeTab === 1 && (
        <DrillDownChart height={550} />
      )}
    </Container>
  );
};

export default Cards;
